<template>
  <div>
    <add-or-update-member-designation-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      :participant-types="memberTypes"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />

    <list-table
      :records="memberDesignations"
      :columns="tableColumns"
      :total-records="totalRecords"
      :current-page-number.sync="currentPage"
      :search-filter.sync="search"
      :loading="loading"
      :error-message="errorMessage"
      :show-search="true"
      :show-filter-by-default="true"
    >
      <template #filters>
        <b-row>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <v-select
              v-model="memberTypeFilter"
              placeholder="Filter by member type"
              :options="memberTypes"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>
          <b-col v-if="memberTypeFilter" cols="12" md="3">
            <b-button variant="flat-secondary" @click="memberTypeFilter = null">
              <feather-icon icon="XIcon" class="mr-50" />
              <span class="align-middle">Clear filters</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <template v-if="can('member_designations:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add New Designation</span>
        </b-button>
      </template>

      <template #cell(_id)="data">
        <div>
          <div
            class="d-flex align-items-center cursor-pointer py-50"
            @click="$set(data.item, '_showDetails', !data.item._showDetails)"
          >
            <feather-icon
              :icon="data.item._showDetails ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="16"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold text-primary">{{ data.item.designation }}</span>
            <b-badge variant="light-warning" pill class="ml-75">
              {{ (data.item.categories || []).length }}
            </b-badge>
          </div>
        </div>
      </template>

      <template #row-details="data">
        <b-card class="mb-1 border" no-body>
          <b-card-body class="p-1">
            <b-table
              v-if="data.item.categories && data.item.categories.length"
              :items="data.item.categories"
              :fields="childColumns"
              small
              bordered
              responsive
              class="mb-0"
            >
              <template #cell(is_cop_area_head)="value">
                <b-badge pill :variant="value.item.is_cop_area_head ? 'light-success' : 'light-secondary'">
                  {{ value.item.is_cop_area_head ? 'Yes' : 'No' }}
                </b-badge>
              </template>

              <template #cell(status)="value">
                <b-badge
                  pill
                  :variant="`light-${resolveStatusVariant(value.item.status)}`"
                  class="text-capitalize"
                >
                  {{ getUserStatusText(value.item.status) }}
                </b-badge>
              </template>

              <template #cell(created_by_user)="child">
                <div v-if="child.item.created_by_user" class="d-flex flex-column">
                  <small class="font-weight-bold">{{ child.item.created_by_user.first_name }} {{ child.item.created_by_user.last_name }}</small>
                  <small v-if="child.item.created" class="text-muted">{{ formatDate(child.item.created, 'dd MMM yyyy') }}</small>
                </div>
                <small v-else class="text-muted">—</small>
              </template>

              <template #cell(updated_by_user)="child">
                <div v-if="child.item.updated_by_user" class="d-flex flex-column">
                  <small class="font-weight-bold">{{ child.item.updated_by_user.first_name }} {{ child.item.updated_by_user.last_name }}</small>
                  <small v-if="child.item.modified" class="text-muted">{{ formatDate(child.item.modified, 'dd MMM yyyy') }}</small>
                </div>
                <small v-else class="text-muted">—</small>
              </template>

              <template #cell(actions)="action">
                <b-dropdown
                  variant="link"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>

                  <b-dropdown-item
                    v-if="can('member_designations:CanUpdate')"
                    @click="onEdit(action.item)"
                  >
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">Edit</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="can('member_designations:CanDelete')"
                    @click="onDelete(action.item._id)"
                  >
                    <feather-icon icon="DeleteIcon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <p v-else class="text-muted mb-0 text-center py-1">
              No designations found for this type.
            </p>
          </b-card-body>
        </b-card>
      </template>
    </list-table>
  </div>
</template>

<script>
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateMemberDesignationModal from "@/@core/components/shared/church/AddOrUpdateMemberDesignationModal.vue";

import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";

import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BDropdown,
  BDropdownItem,
  BOverlay,
  BRow,
  BTable,
} from "bootstrap-vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";

const watchHandler = {
  handler() {
    this.debounceFetchAllData();
  },
  immediate: false,
};

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData();
  },
  immediate: false,
};

export default {
  name: "Designation",
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BDropdown,
    BDropdownItem,
    BOverlay,
    BRow,
    BTable,
    ListTable,
    vSelect,
    AddOrUpdateMemberDesignationModal,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      memberTypeFilter: null,
      memberTypes: [],
      memberDesignations: [],

      search: "",

      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: "_id", label: "Member Type", sortable: false },
      ],

      childColumns: [
        { key: 'designation', label: 'Designation', sortable: false },
        { key: 'is_cop_area_head', label: 'Area Head', sortable: false },
        { key: 'status', sortable: false },
        { key: 'created_by_user', label: 'Created By', sortable: false },
        { key: 'updated_by_user', label: 'Updated By', sortable: false },
        { key: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value);
      },
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value);
      },
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value);
      },
    },
  },
  watch: {
    currentPage: watchHandler,
    memberTypeFilter: watchHandlerRequiresPageReset,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.fetchMemberTypes();
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search = "",
      page = 1,
      limit = this.perPage,
      member_type_id = null,
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
    this.memberTypeFilter = member_type_id;

    this.debounceFetchAllData();
  },
  methods: {
    formatDate,
    async fetchMemberTypes() {
      try {
        const query = { page: 1, limit: 1000 };
        const request = await this.useJwt().mainService.memberTypes.list(query);
        const { data } = request.data;

        this.memberTypes = data.map((member_type) => ({
          label: member_type.title,
          value: member_type._id,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            variant: "danger",
            text: error_message,
            icon: "AlertTriangleIcon",
          },
        });
      }
    },
    async fetchAllData() {
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          limit: this.perPage,
          search: this.search || undefined,
          page: this.currentPage,
          member_type_id: this.memberTypeFilter || undefined,
        };
        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.memberDesignations.list(query);
        const { data, pagination } = request.data;
        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.memberDesignations = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        this.errorMessage = error_message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id) {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Confirm Delete?",
          text: "Do you want to delete this Designation?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, proceed.",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().mainService.memberDesignations.delete(id);
        await this.fetchAllData();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "Designation Deleted Successfully",
          },
        });
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error!",
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: error_message,
            },
          });
        });
      } finally {
        this.loading = false;
      }
    },
    onEdit(update) {
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null;
      }
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
